import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { Component, inject, OnInit } from '@angular/core';
import { ModuleEntity } from '../../../module/module.entity';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { MatLabel } from '@angular/material/form-field';
import {
  getLearningUnitColumns,
  getLoadLearningUnitsFn
} from '../../../learning-unit/learning-unit-relation-table.config';
import { Store } from '@ngxs/store';
import { LearningUnitEntityService } from '../../../learning-unit/services/learning-unit.entity.service';
import { LearningSectionEntityService } from '../../../learning-section/services/learning-section.entity.service';
import { RelationTableSelectComponent } from '../../../utils/component/relation-table/relation-table-select.component';
import { LearningUnitEntity } from '../../../learning-unit/learning-unit-entity';
import { normalizeManyRelations } from '../../../core/decorators/relation.decorator';
import { Observable, map } from 'rxjs';
import { EntityStateModel } from '../../../core/state/entity.state';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'pixl-module-form-dialog',
  templateUrl: './module-form-dialog.component.html',
  imports: [
    MatDialogContent,
    MatFormField,
    MatDialogTitle,
    MatLabel,
    MatFormField,
    MatInput,
    MatFormField,
    MatSelect,
    MatOption,
    MatDialogActions,
    ReactiveFormsModule,
    MatButton,
    RelationTableSelectComponent,
    TranslocoPipe,
  ],
  styleUrls: ['./module-form-dialog.component.scss'],
})
export class ModuleFormDialogComponent implements OnInit {
  private store = inject(Store);
  private learningUnitService = inject(LearningUnitEntityService);
  private learningSectionService = inject(LearningSectionEntityService);

  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<ModuleFormDialogComponent>>(MatDialogRef);

  data? = inject<Partial<ModuleEntity>>(MAT_DIALOG_DATA);

  form: FormGroup;
  learningUnits$!: Observable<LearningUnitEntity[]>;

  // for relation table select
  learningUnitColumns = getLearningUnitColumns(this.store, this.learningSectionService);
  loadLearningUnitsFn = getLoadLearningUnitsFn(this.store, this.learningUnitService);

  selectedLearningUnits: LearningUnitEntity[] = [];
  currentPageLearningUnits = 1;
  pageSizeLearningUnits = 20;

  constructor() {
    const data = this.data;

    this.form = this.fb.group({
      title: [data?.title || '', Validators.required],
      status: [data?.status || 'Edit', Validators.required],
      description: [data?.description || ''],
      preTitle: [data?.preTitle || ''],
      learningUnits: [normalizeManyRelations(data?.learningUnits)],
    });
  }

  ngOnInit() {
    const learningUnitQuery = {
      'page[size]': this.pageSizeLearningUnits,
      'page[number]': this.currentPageLearningUnits,
      'sort': 'title',
    };

    const selectedLearningUnitIds = normalizeManyRelations(
      this.data?.learningUnits,
    ) as string[];

    const state = this.store.selectSnapshot(
      this.learningUnitService.stateClass,
    ) as EntityStateModel<LearningUnitEntity>;
    this.selectedLearningUnits = selectedLearningUnitIds
      .map((id) => state.entities[id])
      .filter((e): e is LearningUnitEntity => !!e);

    this.learningUnits$ = this.store
      .select(this.learningUnitService.allByQuery(learningUnitQuery))
      .pipe(
        map((paginated) => {
          return [...this.selectedLearningUnits, ...paginated].filter(
            (seq, index, self) =>
              index === self.findIndex((s) => s.id === seq.id),
          );
        }),
      );

    this.form
      .get('learningUnits')
      ?.setValue(this.selectedLearningUnits.map((s) => s.id));
  }

  onLearningUnitsChanged(selected: LearningUnitEntity[]): void {
    this.selectedLearningUnits = selected;
    this.form
      .get('learningUnits')
      ?.setValue(selected.map((lu) => lu.id));
  }

  save(): void {
    if (this.form.valid) {
      const formValue = this.form.value;

      const updatedModule = Object.assign(
        new ModuleEntity(),
        { id: this.data?.id },
        formValue,
        {
          learningUnits: (formValue.learningUnits as string[])?.length ?
            (formValue.learningUnits as string[]).map((id) => ({ id })) :
            [],
        },
      );

      this.dialogRef.close(updatedModule);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
